import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { companiesAPI } from '../lib/api';
import { ChevronLeft, ChevronRight, CalendarDays, List, Sun, Moon, Star } from 'lucide-react';
import { PageHero } from '../components/ModernPage';
import { useSettings } from '../App';
import { opportunitySkin } from '../lib/opportunitySkin';

// ─────────────────────────────────────────────────────────────────────────
// Earnings Announcement — month calendar (default) or list of the companies
// reporting earnings (companies_directory.earnings_date, refreshed weekly by
// scripts/earnings-refresh). URL state: ?month=YYYY-MM&view=list&mine=1.
// Calendar = Mon–Fri weeks (markets are closed on weekends; the rare weekend
// date is shown on the list only). Colours = theme tokens, never brand hex.
// ─────────────────────────────────────────────────────────────────────────

const TIMING = { bmo: 'Before market open', amc: 'After market close' };
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const CELL_MAX = 11; // logos per calendar cell; the rest open that day in the list view

const CSS = `
.ea-seg{display:inline-flex;background:#F4F1EA;border:1px solid var(--color-border,#e8dccd);border-radius:999px;padding:4px}
.ea-seg button{border:0;background:transparent;font-weight:600;font-size:13px;color:#8A8F98;padding:7px 14px;
  border-radius:999px;cursor:pointer;display:inline-flex;align-items:center;gap:7px;font-family:inherit;transition:color .15s}
.ea-seg button.on{background:var(--color-bg,#fff);color:var(--color-heading,#11151C);box-shadow:0 2px 8px -4px rgba(17,21,28,.35)}
.ea-nav{width:38px;height:38px;border-radius:999px;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;
  border:1.5px solid var(--color-border,#e8dccd);background:var(--color-bg,#fff);color:var(--color-heading,#11151C);transition:border-color .15s}
.ea-nav:hover{border-color:var(--color-accent,#C9A24B)}
.ea-today{height:38px;padding:0 16px;border-radius:999px;font-weight:600;font-size:13px;cursor:pointer;font-family:inherit;
  border:1.5px solid var(--color-border,#e8dccd);background:var(--color-bg,#fff);color:var(--color-heading,#11151C)}
.ea-today:hover{border-color:var(--color-accent,#C9A24B)}
.ea-cell{border:1px solid var(--color-border,#e8dccd);border-radius:var(--op-radius,12px);background:var(--color-bg,#fff);min-height:132px;padding:10px}
.ea-cell.out{background:#FAF8F3;opacity:.55}
.ea-cell.now{border-color:var(--color-accent,#C9A24B);box-shadow:0 0 0 2px color-mix(in srgb,var(--color-accent,#C9A24B) 25%,transparent)}
.ea-logo{cursor:pointer;font-family:inherit;padding:0;width:46px;height:46px;border-radius:10px;border:1px solid var(--color-border,#e8dccd);background:#fff;display:flex;
  align-items:center;justify-content:center;overflow:hidden;position:relative;transition:transform .15s,border-color .15s}
.ea-logo:hover{transform:translateY(-2px);border-color:var(--color-accent,#C9A24B)}
.ea-logo img{width:100%;height:100%;object-fit:contain;padding:5px}
.ea-logo .tk{font-size:10px;font-weight:800;color:var(--color-heading,#11151C);letter-spacing:-.01em}
.ea-logo .fav{position:absolute;top:2px;right:2px;color:var(--color-accent,#C9A24B)}
.ea-row{display:flex;align-items:center;gap:14px;padding:14px 16px;border-radius:var(--op-radius,12px);text-decoration:none;border:1px solid var(--color-border,#e8dccd);background:var(--color-bg,#fff);transition:border-color .15s,box-shadow .15s}
.ea-row:hover{border-color:var(--color-accent,#C9A24B);box-shadow:0 12px 28px -22px rgba(17,21,28,.5)}
@media (prefers-reduced-motion:reduce){.ea-logo,.ea-logo:hover{transition:none;transform:none}}
`;

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function parseMonth(v) {
  const m = /^(\d{4})-(\d{2})$/.exec(v || '');
  const now = new Date();
  return m ? new Date(+m[1], +m[2] - 1, 1) : new Date(now.getFullYear(), now.getMonth(), 1);
}

// Mon–Fri weeks covering the month (first Monday on/before day 1 → last Friday on/after month end).
function monthWeeks(first) {
  const start = new Date(first);
  start.setDate(1 - ((first.getDay() + 6) % 7));
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  const weeks = [];
  for (let d = new Date(start); d <= last || weeks.length === 0; d.setDate(d.getDate() + 7)) {
    weeks.push(DOW.map((_, i) => { const x = new Date(d); x.setDate(d.getDate() + i); return x; }));
  }
  return weeks;
}

function Logo({ c }) {
  const src = c.logo_on || c.logo_off;
  const tip = `${c.name} (${c.symbol})${TIMING[c.earnings_time] ? ` · ${TIMING[c.earnings_time]}` : ''}`;
  return (
    <Link to={`/companies/${c.slug}`} className="ea-logo" title={tip} aria-label={tip} data-testid={`earnings-logo-${c.slug}`}>
      {src ? <img src={src} alt="" loading="lazy" /> : <span className="tk">{(c.symbol || '?').slice(0, 5)}</span>}
      {c.following && <Star className="fav w-2.5 h-2.5" fill="currentColor" />}
    </Link>
  );
}

function TimingTag({ t }) {
  if (!TIMING[t]) return <span className="text-xs" style={{ color: '#9aa0a6' }}>Time TBC</span>;
  const Icon = t === 'bmo' ? Sun : Moon;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'var(--color-body-text, #4B5563)' }}>
      <Icon className="w-3.5 h-3.5" style={{ color: 'var(--color-accent, #C9A24B)' }} /> {TIMING[t]}
    </span>
  );
}

export default function EarningsCalendarPage() {
  const skin = opportunitySkin(useSettings().active_theme); // s153: expanded layout, per-theme skin
  const [sp, setSp] = useSearchParams();
  const first = parseMonth(sp.get('month'));
  const view = sp.get('view') === 'list' ? 'list' : 'calendar';
  const mine = sp.get('mine') === '1';
  const weeks = useMemo(() => monthWeeks(first), [first.getTime()]); // eslint-disable-line react-hooks/exhaustive-deps
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [followed, setFollowed] = useState(null); // member's portfolio (for the "My Portfolio" empty state)
  useEffect(() => {
    companiesAPI.followed().then(r => setFollowed(r.data.items || [])).catch(() => setFollowed([]));
  }, []);

  const range = [iso(weeks[0][0]), iso(weeks[weeks.length - 1][4])];
  const monthStart = iso(first);
  const monthEnd = iso(new Date(first.getFullYear(), first.getMonth() + 1, 0));
  // List covers the month itself (incl. weekends); calendar covers its padded weeks.
  const fetchStart = range[0] < monthStart ? range[0] : monthStart;
  const fetchEnd = range[1] > monthEnd ? range[1] : monthEnd;

  useEffect(() => {
    setItems(null); setError(false);
    companiesAPI.earnings(fetchStart, fetchEnd)
      .then(r => setItems(r.data.items || []))
      .catch(() => setError(true));
  }, [fetchStart, fetchEnd]);

  const set = (patch) => {
    const next = { month: `${first.getFullYear()}-${pad(first.getMonth() + 1)}`, view, mine: mine ? '1' : '', ...patch };
    const params = {};
    if (next.month) params.month = next.month;
    if (next.view === 'list') params.view = 'list';
    if (next.mine === '1') params.mine = '1';
    setSp(params);
  };
  const shift = (n) => { const d = new Date(first.getFullYear(), first.getMonth() + n, 1); set({ month: `${d.getFullYear()}-${pad(d.getMonth() + 1)}` }); };
  const goToday = () => set({ month: '' });
  // "+N" tile → list view scrolled to that day.
  const openDay = (day) => {
    set({ view: 'list' });
    setTimeout(() => document.getElementById(`ea-day-${day}`)?.scrollIntoView({ block: 'start' }), 60);
  };

  const shown = (items || []).filter(c => !mine || c.following);
  const byDay = useMemo(() => {
    const m = {};
    shown.forEach(c => { (m[c.earnings_date] = m[c.earnings_date] || []).push(c); });
    return m;
  }, [shown]);
  const todayIso = iso(new Date());
  const monthLabel = first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const monthCount = shown.filter(c => c.earnings_date >= monthStart && c.earnings_date <= monthEnd).length;
  const listDays = Object.keys(byDay).filter(d => d >= monthStart && d <= monthEnd).sort();

  return (
    <div data-testid="earnings-page" style={{ ...skin, backgroundColor: 'var(--color-bg, #fff)' }}>
      <style>{CSS}</style>
      <PageHero title="Earning Announcement"
        summary={items ? `${monthCount} compan${monthCount === 1 ? 'y reports' : 'ies report'} earnings in ${monthLabel}${mine ? ' in your portfolio' : ''}.` : 'Upcoming earnings reports of the companies in our research universe.'}
        trail={[{ label: 'Home', to: '/' }, { label: 'Companies', to: '/companies' }, { label: 'Earning Announcement' }]} />
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-12 lg:py-16">
        {/* Toolbar: month nav · portfolio filter · view switch */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2">
            <button type="button" className="ea-nav" onClick={() => shift(-1)} aria-label="Previous month" data-testid="earnings-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button type="button" className="ea-nav" onClick={() => shift(1)} aria-label="Next month" data-testid="earnings-next"><ChevronRight className="w-4 h-4" /></button>
            <span className="text-lg font-extrabold ml-2 mr-1 tracking-[-0.01em]" style={{ fontFamily: 'var(--op-head)', color: 'var(--color-heading, #11151C)' }} data-testid="earnings-month">{monthLabel}</span>
            <button type="button" className="ea-today" onClick={goToday}>Today</button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="ea-seg" role="group" aria-label="Companies">
              <button type="button" className={!mine ? 'on' : ''} onClick={() => set({ mine: '' })}>All</button>
              <button type="button" className={mine ? 'on' : ''} onClick={() => set({ mine: '1' })} data-testid="earnings-mine">
                <Star className="w-3.5 h-3.5" /> My Portfolio
              </button>
            </div>
            <div className="ea-seg" role="group" aria-label="View">
              <button type="button" className={view === 'calendar' ? 'on' : ''} onClick={() => set({ view: 'calendar' })} data-testid="earnings-view-calendar">
                <CalendarDays className="w-4 h-4" /> Calendar
              </button>
              <button type="button" className={view === 'list' ? 'on' : ''} onClick={() => set({ view: 'list' })} data-testid="earnings-view-list">
                <List className="w-4 h-4" /> List
              </button>
            </div>
          </div>
        </div>

        {error && <p className="text-sm py-10 text-center" style={{ color: '#b91c1c' }}>Could not load the earnings calendar.</p>}
        {!items && !error && <p className="text-sm py-10 text-center" style={{ color: '#9aa0a6' }}>Loading…</p>}

        {items && mine && monthCount === 0 && followed && (
          <div className="rounded-xl p-5 mb-6" style={{ border: '1px solid var(--color-border, #e8dccd)', backgroundColor: '#FAF8F3' }} data-testid="earnings-mine-empty">
            {followed.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--color-body-text, #4B5563)' }}>
                You don't follow any company yet. Open a company and press <strong>Follow</strong> to see its earnings here.
              </p>
            ) : (
              <>
                <p className="text-sm mb-3" style={{ color: 'var(--color-body-text, #4B5563)' }}>
                  None of the {followed.length} compan{followed.length === 1 ? 'y' : 'ies'} in your portfolio report{followed.length === 1 ? 's' : ''} earnings in {monthLabel}. Next dates:
                </p>
                <div className="flex flex-wrap gap-2">
                  {followed.map(c => {
                    const ok = /^\d{4}-\d{2}-\d{2}$/.test(c.earnings_date || '') && c.earnings_date >= todayIso;
                    const label = ok ? new Date(...c.earnings_date.split('-').map((v, i) => +v - (i === 1 ? 1 : 0))).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Date not announced';
                    return ok ? (
                      <button key={c.slug} type="button" onClick={() => set({ month: c.earnings_date.slice(0, 7) })}
                        className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[13px] font-semibold"
                        style={{ border: '1.5px solid var(--color-border, #e8dccd)', background: 'var(--color-bg, #fff)', color: 'var(--color-heading, #11151C)' }}>
                        {c.symbol} · {label}
                      </button>
                    ) : (
                      <span key={c.slug} className="inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px]" style={{ border: '1.5px dashed var(--color-border, #e8dccd)', color: '#8A8F98' }}>
                        {c.symbol} · {label}
                      </span>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {items && view === 'calendar' && (
          <div data-testid="earnings-calendar">
            <div className="hidden md:grid grid-cols-5 gap-3 mb-2">
              {DOW.map(d => <div key={d} className="text-[11px] uppercase tracking-[0.2em] font-bold px-1" style={{ color: '#8A8F98' }}>{d}</div>)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {weeks.flat().map(d => {
                const key = iso(d);
                const inMonth = d.getMonth() === first.getMonth();
                const list = byDay[key] || [];
                return (
                  <div key={key} className={`ea-cell ${inMonth ? '' : 'out hidden md:block'} ${key === todayIso ? 'now' : ''}`} data-testid={`earnings-day-${key}`}>
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-[13px] font-bold" style={{ color: 'var(--color-heading, #11151C)' }}>
                        <span className="md:hidden">{DOW[(d.getDay() + 6) % 7]}, </span>
                        {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      {list.length > 0 && <span className="text-[11px] font-semibold" style={{ color: '#8A8F98' }}>{list.length}</span>}
                    </div>
                    {list.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {list.slice(0, CELL_MAX).map(c => <Logo key={c.slug} c={c} />)}
                        {list.length > CELL_MAX && (
                          <button type="button" className="ea-logo" onClick={() => openDay(key)} aria-label={`Show all ${list.length} companies on ${key}`} data-testid={`earnings-more-${key}`}>
                            <span className="tk">+{list.length - CELL_MAX}</span>
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs" style={{ color: '#B4B8BE' }}>No earnings</p>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs mt-4 flex items-center gap-1.5" style={{ color: '#8A8F98' }}>
              <Star className="w-3 h-3" fill="currentColor" style={{ color: 'var(--color-accent, #C9A24B)' }} /> Company in your portfolio · hover a logo for the report timing
            </p>
          </div>
        )}

        {items && view === 'list' && (
          <div data-testid="earnings-list">
            {listDays.length === 0 && <p className="text-sm py-10 text-center" style={{ color: '#9aa0a6' }}>No earnings announcements this month.</p>}
            {listDays.map(day => {
              const [y, m, dd] = day.split('-').map(Number);
              const label = new Date(y, m - 1, dd).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
              return (
                <section key={day} id={`ea-day-${day}`} className="mb-7" style={{ scrollMarginTop: 110 }}>
                  <h2 className="flex items-baseline gap-3 text-[15px] font-extrabold pb-2 mb-1 border-b" style={{ fontFamily: 'var(--op-head)', color: 'var(--color-heading, #11151C)', borderColor: 'var(--color-border, #e8dccd)' }}>
                    {label}{day === todayIso && <span className="text-[11px] uppercase tracking-[0.2em]" style={{ color: 'var(--color-accent, #C9A24B)' }}>Today</span>}
                    <span className="ml-auto text-xs font-semibold" style={{ color: '#8A8F98' }}>{byDay[day].length}</span>
                  </h2>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 mt-3">
                  {byDay[day].map(c => (
                    <Link key={c.slug} to={`/companies/${c.slug}`} className="ea-row" data-testid={`earnings-row-${c.slug}`}>
                      <span className="ea-logo shrink-0" style={{ width: 56, height: 56 }}>
                        {(c.logo_on || c.logo_off) ? <img src={c.logo_on || c.logo_off} alt="" loading="lazy" /> : <span className="tk">{(c.symbol || '?').slice(0, 5)}</span>}
                      </span>
                      <span className="min-w-0 flex-1 flex flex-col gap-0.5">
                        <span className="flex items-center gap-2 font-bold text-[15px] truncate" style={{ fontFamily: 'var(--op-head)', color: 'var(--color-heading, #11151C)' }}>
                          <span className="truncate">{c.name}</span>{c.following && <Star className="w-3.5 h-3.5 shrink-0" fill="currentColor" style={{ color: 'var(--color-accent, #C9A24B)' }} />}
                        </span>
                        <span className="block text-xs truncate" style={{ color: '#8A8F98' }}>{c.symbol}{c.sector ? ` · ${c.sector}` : ''}</span>
                        <span className="mt-1"><TimingTag t={c.earnings_time} /></span>
                      </span>
                    </Link>
                  ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
